import * as React from "react";
import { Trophy, Flame, Medal, Crown, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import { PfpAvatar } from "@/components/pfp-avatar";
import { useAuth } from "@/lib/auth-context";
import { fetchLeaderboard, type LeaderboardRow } from "@/lib/queries";

type Mode = "points" | "streak";

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [rows, setRows] = React.useState<LeaderboardRow[]>([]);
  const [mode, setMode] = React.useState<Mode>("points");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchLeaderboard(50);
      if (!cancelled) { setRows(r); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const metric = mode === "streak" ? "streak" : "total_points";
  const sorted = React.useMemo(() =>
    [...rows].filter(r => r[metric] > 0).sort((a, b) => b[metric] - a[metric]),
    [rows, metric]
  );
  const podium = sorted.slice(0, 3);
  const remaining = sorted.slice(3, 50);

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[800px] pointer-events-none" />

        <div className="relative mx-auto max-w-6xl px-4 md:px-6 pt-10 pb-32">
          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Leaderboard</h1>
              <Badge variant="accent" className="ml-auto"><Flame className="w-3 h-3" /> Live</Badge>
            </div>
            <p className="text-fg-2">Top players right now. Climb by drilling vocab and keeping your streak alive.</p>

            <div className="mt-5 inline-flex border border-line rounded-full p-1 bg-bg-2">
              {(["points","streak"] as Mode[]).map(m => (
                <button key={m} onClick={() => setMode(m)}
                  className={
                    "px-4 h-8 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors " +
                    (mode === m ? "bg-accent text-bg" : "text-fg-2 hover:text-fg")
                  }>{m === "points" ? "Points" : "Streak"}</button>
              ))}
            </div>
          </BlurReveal>

          {loading ? (
            <div className="mt-12 flex items-center justify-center gap-2 text-fg-3">
              <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading…
            </div>
          ) : sorted.length === 0 ? (
            <Card className="mt-12 text-center">
              <CardContent className="py-12">
                <p className="text-fg-2">No one's scored yet. Be the first to put points on the board.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <BlurReveal delay={0.1}>
                <section className="mt-8 grid grid-cols-3 gap-3">
                  {podium.map((p, i) => {
                    const value = mode === "streak" ? p.streak : p.total_points;
                    const me = p.id === user?.id;
                    return (
                      <div key={p.id}
                        className={
                          "rounded-3xl border p-5 text-center backdrop-blur-sm " +
                          (i === 0 ? "bg-gradient-to-b from-yellow-400/20 to-transparent border-yellow-400/40 md:order-2 md:-translate-y-3"
                            : i === 1 ? "bg-gradient-to-b from-slate-300/15 to-transparent border-slate-300/30 md:order-1"
                            : "bg-gradient-to-b from-amber-600/20 to-transparent border-amber-600/30 md:order-3") +
                          (me ? " ring-1 ring-accent" : "")
                        }>
                        <div className="flex justify-center mb-3 relative">
                          <PfpAvatar size="lg" url={p.avatar_url} name={p.full_name} seed={p.id} />
                          <span className="absolute -top-2 -right-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-bg border border-line">
                            {i === 0 ? <Crown className="w-4 h-4 text-yellow-400" />
                              : i === 1 ? <Medal className="w-4 h-4 text-slate-200" />
                              : <Medal className="w-4 h-4 text-amber-500" />}
                          </span>
                        </div>
                        <div className="font-semibold truncate">{p.full_name}{me && <span className="text-fg-3 text-xs"> · you</span>}</div>
                        <div className="mt-2 text-2xl font-bold text-accent">
                          {mode === "streak" ? "🔥 " : ""}{value.toLocaleString()}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-fg-3">
                          {mode === "streak" ? "days" : "points"}
                        </div>
                      </div>
                    );
                  })}
                </section>
              </BlurReveal>

              <BlurReveal delay={0.2}>
                <Card className="mt-8 p-0 overflow-hidden">
                  <header className="px-5 py-4 flex items-center gap-3 border-b border-line bg-bg-3/30">
                    <h2 className="font-semibold text-sm">Top {Math.min(50, sorted.length)}</h2>
                    <Separator orientation="vertical" className="h-4" />
                    <span className="text-xs text-fg-3">By {mode === "streak" ? "longest streak" : "total points"}</span>
                  </header>
                  <CardContent className="p-0">
                    <ol className="divide-y divide-line">
                      {remaining.map((r, i) => {
                        const me = r.id === user?.id;
                        const value = mode === "streak" ? r.streak : r.total_points;
                        return (
                          <li key={r.id} className={
                            "grid grid-cols-[2.5rem_auto_1fr_auto] items-center gap-4 px-5 py-3 " +
                            (me ? "bg-accent/10" : "")
                          }>
                            <span className="text-sm font-bold text-fg-3 text-center">#{i + 4}</span>
                            <PfpAvatar size="sm" url={r.avatar_url} name={r.full_name} seed={r.id} />
                            <div className="min-w-0">
                              <div className={"text-sm truncate " + (me ? "font-semibold text-fg" : "")}>
                                {r.full_name}{me && <span className="text-xs text-fg-3"> · you</span>}
                              </div>
                            </div>
                            <span className="text-sm font-bold text-accent">
                              {mode === "streak" ? "🔥 " : ""}{value.toLocaleString()}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  </CardContent>
                </Card>
              </BlurReveal>
            </>
          )}
        </div>
      </div>
    </PageFrame>
  );
}
